import React, { useState, useEffect } from 'react';
import { 
  Box, Container, Typography, Card, CardContent, Grid, TextField, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Avatar, Button, IconButton, CircularProgress, Chip,
  InputAdornment, Dialog, DialogTitle, DialogContent,
  DialogActions, FormControl, InputLabel, Select, MenuItem,
  FormControlLabel, Switch, useMediaQuery, useTheme,
  Snackbar, Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PeopleIcon from '@mui/icons-material/People';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/api';
import { exportToExcel } from '../utils/excel';

interface UserItem {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  phone: string;
  role: 'admin' | 'coordinator' | 'teacher' | 'student';
  rut: string | null;
  moodle_id: number | null;
  moodle_username: string;
  is_active: boolean;
  date_joined: string;
}

export const UsersPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [users, setUsers] = useState<UserItem[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editingUser, setEditingUser] = useState<UserItem | null>(null);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<any>({});
  
  // Form fields
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    password2: '',
    role: 'student' as any,
    rut: '',
    phone: '',
    is_active: true
  });

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/auth/users/');
      if (response.data && response.data.results) {
        setUsers(response.data.results);
        setFilteredUsers(response.data.results);
      }
    } catch (err: any) {
      console.error(err);
      setToast({ open: true, message: 'Fallo al cargar usuarios.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      fetchUsers();
    }
  }, [currentUser]);

  // Handle Search and Filter
  useEffect(() => {
    let result = users;
    
    if (search) {
      const term = search.toLowerCase();
      result = result.filter(u => 
        u.full_name.toLowerCase().includes(term) ||
        u.email.toLowerCase().includes(term) ||
        (u.rut && u.rut.toLowerCase().includes(term))
      );
    }
    
    if (roleFilter !== 'all') {
      result = result.filter(u => u.role === roleFilter);
    }
    
    setFilteredUsers(result);
  }, [search, roleFilter, users]);

  if (currentUser?.role !== 'admin') {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }} className="fade-in-up">
        <Card variant="outlined" sx={{ p: 4, borderRadius: 4 }}>
          <Typography variant="h5" color="error" fontWeight={700} gutterBottom>
            Acceso Denegado
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            No tienes los permisos de Administrador requeridos para gestionar usuarios en el sistema.
          </Typography>
          <Button variant="contained" color="secondary" href="/" sx={{ mt: 2 }}>
            Volver al Dashboard
          </Button>
        </Card>
      </Container>
    );
  }

  const handleOpenCreate = () => {
    setEditingUser(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      password2: '',
      role: 'student',
      rut: '',
      phone: '',
      is_active: true
    });
    setErrors({});
    setOpenDialog(true);
  };

  const handleOpenEdit = (userItem: UserItem) => {
    setEditingUser(userItem);
    setFormData({
      first_name: userItem.first_name || '',
      last_name: userItem.last_name || '',
      email: userItem.email || '',
      password: '', // Empty on edit
      password2: '',
      role: userItem.role || 'student',
      rut: userItem.rut || '',
      phone: userItem.phone || '',
      is_active: userItem.is_active
    });
    setErrors({});
    setOpenDialog(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, is_active: e.target.checked }));
  };

  const handleSelectChange = (e: any) => {
    setFormData(prev => ({ ...prev, role: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});

    try {
      if (editingUser) {
        // Edit User (PATCH)
        const payload: any = {
          first_name: formData.first_name,
          last_name: formData.last_name,
          rut: formData.rut || null,
          phone: formData.phone,
          role: formData.role,
          is_active: formData.is_active
        };
        
        await apiClient.patch(`/auth/users/${editingUser.id}/`, payload);
        setToast({ open: true, message: 'Usuario actualizado con éxito.', severity: 'success' });
      } else {
        // Create User (POST)
        const payload = { ...formData };
        if (payload.rut === '') delete (payload as any).rut;
        
        await apiClient.post('/auth/users/', payload);
        setToast({ open: true, message: 'Usuario creado con éxito.', severity: 'success' });
      }
      setOpenDialog(false);
      fetchUsers();
    } catch (err: any) {
      console.error(err);
      if (err.response?.data) {
        setErrors(err.response.data);
        const detail = err.response.data.detail || 'Fallo al procesar formulario. Verifica los campos.';
        setToast({ open: true, message: detail, severity: 'error' });
      } else {
        setToast({ open: true, message: 'Error de conexión con el servidor.', severity: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (userItem: UserItem) => {
    try {
      await apiClient.patch(`/auth/users/${userItem.id}/`, {
        is_active: !userItem.is_active
      });
      setToast({ 
        open: true, 
        message: `Usuario ${userItem.is_active ? 'desactivado' : 'activado'} con éxito.`, 
        severity: 'success' 
      });
      fetchUsers();
    } catch (err) {
      console.error(err);
      setToast({ open: true, message: 'No se pudo cambiar el estado del usuario.', severity: 'error' });
    }
  };

  const handleExportExcel = () => {
    const headers = ['ID', 'Email', 'Nombre Completo', 'Rol', 'RUT', 'Moodle Username', 'Moodle ID', 'Activo', 'Fecha Registro'];
    const keys = ['id', 'email', 'full_name', 'role', 'rut', 'moodle_username', 'moodle_id', 'is_active', 'date_joined'];
    exportToExcel(filteredUsers, headers, keys, 'Directorio_Usuarios_CreaSync');
  };

  const getRoleChip = (role: string) => {
    switch (role) {
      case 'admin':
        return <Chip label="Admin" size="small" sx={{ bgcolor: 'rgba(124, 77, 255, 0.12)', color: '#7C4DFF', fontWeight: 700 }} />;
      case 'coordinator':
        return <Chip label="Coordinador" size="small" sx={{ bgcolor: 'rgba(0, 82, 255, 0.12)', color: '#0052FF', fontWeight: 700 }} />;
      case 'teacher':
        return <Chip label="Docente" size="small" sx={{ bgcolor: 'rgba(0, 217, 165, 0.12)', color: '#00B074', fontWeight: 700 }} />;
      case 'student':
        return <Chip label="Estudiante" size="small" sx={{ bgcolor: 'rgba(108, 124, 140, 0.12)', color: '#6C7C8C', fontWeight: 700 }} />;
      default:
        return <Chip label={role} size="small" />;
    }
  };

  return (
    <Container maxWidth="lg" className="fade-in-up" sx={{ py: 4, position: 'relative' }}>
      {/* Dynamic breathing background glow */}
      <Box 
        sx={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          width: 350,
          height: 350,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(124,77,255,0.06) 0%, rgba(0,112,224,0.01) 70%)',
          filter: 'blur(40px)',
          zIndex: -1,
          animation: 'breathing-nebula 7s ease-in-out infinite'
        }}
      />
      
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" mb={4} gap={2}>
        <Box display="flex" alignItems="center">
          <IconButton href="/" sx={{ mr: 2, bgcolor: 'background.paper', border: '1px solid #E2E8F0' }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#0A2540' }}>
              <PeopleIcon color="primary" sx={{ fontSize: 36 }} />
              Gestión de Usuarios
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Crea, edita y administra los accesos y roles institucionales
            </Typography>
          </Box>
        </Box>
        
        <Box display="flex" gap={1.5} width={{ xs: '100%', sm: 'auto' }}>
          <Button
            variant="outlined"
            onClick={handleExportExcel}
            startIcon={<FileDownloadIcon />}
            fullWidth={isMobile}
            sx={{ 
              borderColor: '#00B074', 
              color: '#00B074', 
              borderRadius: 30,
              '&:hover': {
                borderColor: '#008C5C',
                backgroundColor: 'rgba(0, 176, 116, 0.05)'
              }
            }}
          >
            Exportar a Excel
          </Button>
          <Button
            variant="contained"
            onClick={handleOpenCreate}
            startIcon={<PersonAddIcon />}
            fullWidth={isMobile}
            sx={{
              borderRadius: 30,
              background: 'linear-gradient(135deg, #7C4DFF 0%, #0070E0 100%)',
              boxShadow: '0 4px 12px rgba(124, 77, 255, 0.2)',
              '&:hover': {
                background: 'linear-gradient(135deg, #6c3ce6 0%, #0060c0 100%)',
                boxShadow: '0 6px 16px rgba(124, 77, 255, 0.3)'
              }
            }}
          >
            Nuevo Usuario
          </Button>
        </Box>
      </Box>

      {/* Filter and stats */}
      <Card variant="outlined" sx={{ mb: 4, backdropFilter: 'blur(8px)', bgcolor: 'rgba(255, 255, 255, 0.8)' }}>
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={7}>
              <TextField
                fullWidth
                placeholder="Buscar por nombre, correo electrónico o RUT..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  sx: { borderRadius: 3 }
                }}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <Box display="flex" gap={2}>
                <FormControl fullWidth>
                  <InputLabel id="role-filter-label">Filtrar por Rol</InputLabel>
                  <Select
                    labelId="role-filter-label"
                    value={roleFilter}
                    label="Filtrar por Rol"
                    onChange={(e) => setRoleFilter(e.target.value)}
                    sx={{ borderRadius: 3 }}
                  >
                    <MenuItem value="all">Todos los Roles</MenuItem>
                    <MenuItem value="admin">Administradores</MenuItem>
                    <MenuItem value="coordinator">Coordinadores</MenuItem>
                    <MenuItem value="teacher">Docentes</MenuItem>
                    <MenuItem value="student">Estudiantes</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading ? (
        <Box display="flex" justifyContent="center" py={12}>
          <CircularProgress color="secondary" />
        </Box>
      ) : filteredUsers.length === 0 ? (
        <Card variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: 4 }}>
          <Typography variant="h6" color="text.secondary">
            No se encontraron usuarios
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Prueba ajustando la búsqueda o el filtro de rol.
          </Typography>
        </Card>
      ) : isMobile ? (
        // Mobile-native App visual list cards
        <Box display="flex" flexDirection="column" gap={2}>
          {filteredUsers.map(userItem => (
            <Card key={userItem.id} variant="outlined" sx={{ borderRadius: 3, p: 1, position: 'relative' }}>
              <Box display="flex" alignItems="center" gap={2} p={1.5}>
                <Avatar sx={{ bgcolor: userItem.is_active ? 'secondary.main' : '#CBD5E1', width: 44, height: 44 }}>
                  {userItem.first_name[0]}{userItem.last_name[0]}
                </Avatar>
                <Box flexGrow={1}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#0A2540', lineHeight: 1.2 }}>
                    {userItem.full_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem', mt: 0.2 }}>
                    {userItem.email}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={1}>
                    {getRoleChip(userItem.role)}
                    <Chip 
                      label={userItem.is_active ? 'Activo' : 'Suspendido'} 
                      size="small" 
                      color={userItem.is_active ? 'success' : 'default'} 
                      variant={userItem.is_active ? 'light' as any : 'outlined'}
                      sx={{ fontSize: '0.75rem', height: 20 }}
                    />
                  </Box>
                </Box>
                <Box display="flex" flexDirection="column" gap={0.5}>
                  <IconButton color="primary" size="small" onClick={() => handleOpenEdit(userItem)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton 
                    color={userItem.is_active ? 'error' : 'success'} 
                    size="small" 
                    onClick={() => handleToggleActive(userItem)}
                  >
                    {userItem.is_active ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                  </IconButton>
                </Box>
              </Box>
            </Card>
          ))}
        </Box>
      ) : (
        // Desktop Datatable
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 4, overflow: 'hidden' }}>
          <Table>
            <TableHead sx={{ bgcolor: 'rgba(241, 245, 249, 0.6)' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Usuario</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Correo</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>RUT</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Rol</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Sincronización Moodle</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Estado</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((userItem) => (
                <TableRow key={userItem.id} hover sx={{ '&:last-child cell': { border: 0 } }}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar sx={{ width: 36, height: 36, bgcolor: '#0070E0' }}>
                        {userItem.first_name[0]}{userItem.last_name[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={700} sx={{ color: '#0A2540' }}>
                          {userItem.full_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Telf: {userItem.phone || 'No registrado'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{userItem.email}</TableCell>
                  <TableCell>{userItem.rut || '-'}</TableCell>
                  <TableCell>{getRoleChip(userItem.role)}</TableCell>
                  <TableCell>
                    {userItem.moodle_id ? (
                      <Box>
                        <Chip label={`ID: ${userItem.moodle_id}`} size="small" variant="outlined" sx={{ height: 22, fontSize: '0.75rem' }} />
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.2 }}>
                          User: {userItem.moodle_username}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No enlazado
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={userItem.is_active ? 'Activo' : 'Suspendido'} 
                      size="small" 
                      color={userItem.is_active ? 'success' : 'default'} 
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar detalles de usuario" arrow>
                      <IconButton color="primary" onClick={() => handleOpenEdit(userItem)} sx={{ mr: 1 }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={userItem.is_active ? "Suspender acceso" : "Reactivar acceso"} arrow>
                      <IconButton 
                        color={userItem.is_active ? 'error' : 'success'} 
                        onClick={() => handleToggleActive(userItem)}
                      >
                        {userItem.is_active ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create / Edit Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => !saving && setOpenDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 4, p: 1 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" fontWeight={700} sx={{ color: '#0A2540' }}>
            {editingUser ? 'Editar Cuenta de Usuario' : 'Crear Nueva Cuenta'}
          </Typography>
          <IconButton onClick={() => setOpenDialog(false)} disabled={saving}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent dividers sx={{ py: 3 }}>
            <Grid container spacing={3.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nombre(s)"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleInputChange}
                  required
                  error={!!errors.first_name}
                  helperText={errors.first_name?.[0]}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Apellidos"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleInputChange}
                  required
                  error={!!errors.last_name}
                  helperText={errors.last_name?.[0]}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Correo Electrónico"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={!!editingUser}
                  error={!!errors.email}
                  helperText={errors.email?.[0]}
                />
              </Grid>

              {/* Only show passwords if creating new user */}
              {!editingUser && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Contraseña"
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                      error={!!errors.password}
                      helperText={errors.password?.[0]}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Repetir Contraseña"
                      name="password2"
                      type="password"
                      value={formData.password2}
                      onChange={handleInputChange}
                      required
                      error={!!errors.password2}
                      helperText={errors.password2?.[0]}
                    />
                  </Grid>
                </>
              )}

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel id="dialog-role-label">Rol del Sistema</InputLabel>
                  <Select
                    labelId="dialog-role-label"
                    value={formData.role}
                    label="Rol del Sistema"
                    onChange={handleSelectChange}
                  >
                    <MenuItem value="student">Estudiante</MenuItem>
                    <MenuItem value="teacher">Docente</MenuItem>
                    <MenuItem value="coordinator">Coordinador Académico</MenuItem>
                    <MenuItem value="admin">Administrador</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="RUT (ej: 12.345.678-9)"
                  name="rut"
                  value={formData.rut}
                  onChange={handleInputChange}
                  error={!!errors.rut}
                  helperText={errors.rut?.[0]}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Teléfono"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  error={!!errors.phone}
                  helperText={errors.phone?.[0]}
                />
              </Grid>

              {editingUser && (
                <Grid item xs={12} sm={6} display="flex" alignItems="center">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_active}
                        onChange={handleSwitchChange}
                        color="secondary"
                      />
                    }
                    label="Usuario Activo"
                  />
                </Grid>
              )}
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setOpenDialog(false)} color="inherit" disabled={saving}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="secondary"
              disabled={saving}
              sx={{ borderRadius: 30, px: 4 }}
            >
              {saving ? <CircularProgress size={20} color="inherit" /> : editingUser ? 'Guardar Cambios' : 'Crear Usuario'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar 
        open={toast.open} 
        autoHideDuration={5000} 
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={toast.severity} variant="filled" sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

// Tooltip helper
const Tooltip: React.FC<{ title: string; arrow?: boolean; children: React.ReactElement }> = ({ title, children }) => {
  return <span title={title}>{children}</span>;
};
